import React, { useCallback } from "react";
import moment from "moment";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FormatDurationInSeconds } from "go-core/components/Formatters/FormatDate";
import {
	ItemOrderType,
	OrderPreparationStatusChangesApi,
} from "../../../../../../../../../../services/Api/Organization/types";
import { OrderPreviewPreparationStatusName } from "../../services/types";
import OrderPreparationStatusesItem from "./OrderPreparationStatusesItem";

interface Props {
	preparationStatusChanges: OrderPreparationStatusChangesApi;
	orderType: ItemOrderType;
}

const OrderPreparationStatuses = ({ preparationStatusChanges, orderType }: Props) => {
	const { t } = useTranslation();

	const getPreparationStatuses = useCallback(() => {
		const statuses: {
			time: string;
			preparationStatus: OrderPreviewPreparationStatusName;
			duration?: number;
		}[] = [];

		if (preparationStatusChanges.created_at) {
			statuses.push({
				time: preparationStatusChanges.created_at,
				preparationStatus: OrderPreviewPreparationStatusName.NEW,
			});
		}
		if (preparationStatusChanges.accepted_at) {
			statuses.push({
				time: preparationStatusChanges.accepted_at,
				preparationStatus: OrderPreviewPreparationStatusName.ACCEPTED,
			});
		}
		if (preparationStatusChanges.in_progress_at) {
			statuses.push({
				time: preparationStatusChanges.in_progress_at,
				preparationStatus: OrderPreviewPreparationStatusName.IN_PROGRESS,
			});
		}
		if (preparationStatusChanges.ready_at) {
			statuses.push({
				time: preparationStatusChanges.ready_at,
				preparationStatus: OrderPreviewPreparationStatusName.READY,
			});
		}
		if (preparationStatusChanges.packed_at) {
			statuses.push({
				time: preparationStatusChanges.packed_at,
				preparationStatus: OrderPreviewPreparationStatusName.PACKED,
			});
		}
		if (
			preparationStatusChanges.in_delivery_at &&
			(orderType === ItemOrderType.DELIVERY || orderType === ItemOrderType.ROOM_SERVICE)
		) {
			statuses.push({
				time: preparationStatusChanges.in_delivery_at,
				preparationStatus: OrderPreviewPreparationStatusName.IN_DELIVERY,
			});
		}
		if (
			preparationStatusChanges.received_at &&
			orderType !== ItemOrderType.DELIVERY &&
			orderType !== ItemOrderType.ROOM_SERVICE
		) {
			statuses.push({
				time: preparationStatusChanges.received_at,
				preparationStatus: OrderPreviewPreparationStatusName.RECEIVED,
			});
		}
		if (preparationStatusChanges.delivered_at) {
			statuses.push({
				time: preparationStatusChanges.delivered_at,
				preparationStatus: OrderPreviewPreparationStatusName.DELIVERED,
			});
		}

		statuses.forEach((status, index) => {
			const nextStatus = statuses[index + 1];
			if (nextStatus) {
				statuses[index] = {
					...statuses[index],
					duration: getCalculatedDurationTime(status.time, nextStatus.time),
				};
			}
		});

		return statuses;
	}, [preparationStatusChanges]);

	const getCalculatedDurationTime = (statusDate: string, nextStatusDate: string) => {
		const date1 = moment(statusDate);
		const date2 = moment(nextStatusDate);

		return moment.duration(date2.diff(date1)).asSeconds();
	};

	if (getPreparationStatuses().length === 0) return <></>;

	return (
		<Card className="order-preparation-statuses">
			<Card.Body>
				<h5>{t("modules.order.field.preparation_statuses.title")}</h5>
				<div className="table-responsive">
					<table className="table table-preview">
						<thead>
							<tr>
								<th />
								<th>{t("modules.order.field.preparation_status.title")}</th>
								<th>{t("lib:common.word.hour")}</th>
								<th>{t("modules.order.field.duration.title")}</th>
							</tr>
						</thead>
						<tbody>
							{getPreparationStatuses().map((preparationStatus, index) => (
								<OrderPreparationStatusesItem
									preparationStatus={preparationStatus.preparationStatus}
									time={preparationStatus.time}
									duration={preparationStatus.duration}
									key={preparationStatus.preparationStatus}
									lastElement={index === getPreparationStatuses().length - 1}
								/>
							))}
						</tbody>
					</table>
					{preparationStatusChanges.total_time_in_seconds ? (
						<div className="w-100 text-end mt-3">
							<strong>{`${t("modules.order.field.duration.title")}: ${FormatDurationInSeconds(
								preparationStatusChanges.total_time_in_seconds,
								t
							)}`}</strong>
						</div>
					) : (
						<></>
					)}
				</div>
			</Card.Body>
		</Card>
	);
};

export default OrderPreparationStatuses;
